/**
 * Fetches busy events for the selected meeting rooms and adapts them
 * to the `BusyEvent[]` shape consumed by the availability algorithms.
 *
 * Room busy events are merged with interviewer busy events so the gap
 * finder subtracts both — a slot is only "available" when no human
 * AND no room has a conflict.
 */
import { useMemo } from 'react';
import {
  useMeetingRoomBusyEventsQuery,
  useMeetingRoomBusyPrefetch,
} from '@/api/meetingRoomCalendar';
import type { BusyEvent } from '../../availability';
import { EVENT_STYLES } from '../events/eventStyles';
import type { ManualCalendarExternalEvent } from './useManualCalendarEvents';

const ROOM_USER_ID_SENTINEL = 'meeting-room';

export interface UseMeetingRoomBusyEventsArgs {
  roomEmails: string[];
  /** `email → display name`; powers the friendly title on the calendar
   *  block (actual meeting subject lives in the tooltip instead). */
  roomNamesByEmail?: Record<string, string>;
  visibleWeekStartIso: string;
  visibleWeekEndIso: string;
  timezone: string;
  atsJobId?: number;
  enabled: boolean;
  /** Mirror interviewer chunks: visible week + N-1 silent prefetches. */
  prefetchWeeks?: number;
  lookaheadPrefetchWeeks?: number;
}

export interface UseMeetingRoomBusyEventsResult {
  /** Shape fed into the gap-finder (subtracted from common availability). */
  events: BusyEvent[];
  /** FullCalendar-ready external events with the meeting-room palette —
   *  rendered on the calendar so users see the conflict. */
  externalEvents: ManualCalendarExternalEvent[];
  /** `true` while the visible week's query is still loading — caller
   *  should show the loader overlay over the calendar. */
  isBlockingBusyFetch: boolean;
  isLoading: boolean;
}

export function useMeetingRoomBusyEvents({
  roomEmails,
  roomNamesByEmail = {},
  visibleWeekStartIso,
  visibleWeekEndIso,
  timezone,
  atsJobId,
  enabled,
  prefetchWeeks = 3,
  lookaheadPrefetchWeeks = 1,
}: UseMeetingRoomBusyEventsArgs): UseMeetingRoomBusyEventsResult {
  const query = useMeetingRoomBusyEventsQuery({
    roomEmails,
    fromIso: visibleWeekStartIso,
    toIso: visibleWeekEndIso,
    timezone,
    ...(atsJobId !== undefined ? { atsJobId } : {}),
    enabled,
  });
  const { data, isLoading, isFetching, isSuccess } = query;

  // Warm the next few weeks silently so forward navigation is a cache
  // hit — same pattern as `useInterviewerSchedulePrefetch`.
  useMeetingRoomBusyPrefetch({
    anchorWeekStartIso: visibleWeekStartIso,
    timezone,
    roomEmails,
    ...(atsJobId !== undefined ? { atsJobId } : {}),
    prefetchWeeks,
    lookaheadPrefetchWeeks,
    enabled,
  });

  const events = useMemo<BusyEvent[]>(() => {
    if (!data?.events.length) return [];
    return data.events.map((evt, idx) => ({
      id: evt.id ?? `room-${evt.roomEmail}-${idx}`,
      start: evt.start,
      end: evt.end,
      title: evt.title,
      // Tag with a non-numeric sentinel so pastel pickers ignore these
      // but the gap-finder still treats them as busy blocks.
      interviewerId: `${ROOM_USER_ID_SENTINEL}:${evt.roomEmail}`,
    }));
  }, [data]);

  const externalEvents = useMemo<ManualCalendarExternalEvent[]>(() => {
    if (!data?.events.length) return [];
    return data.events.map((evt, idx) => {
      const roomName = roomNamesByEmail[evt.roomEmail];
      const friendlyTitle = roomName ?? 'Meeting room busy';
      // Keep the actual meeting subject (if any) in the tooltip so
      // users can still see what blocked the room.
      const tooltipTitle =
        evt.title && evt.title !== roomName
          ? `${friendlyTitle} · ${evt.title}`
          : friendlyTitle;
      return {
        id: `room-busy-${evt.id ?? `${evt.roomEmail}-${idx}`}`,
        start: evt.start,
        end: evt.end,
        title: friendlyTitle,
        tooltipTitle,
        // Pastel-5 palette so rooms visually sit beneath interviewer
        // pastels (see `EVENT_STYLES.meetingRoom` / legacy parity).
        backgroundColor: EVENT_STYLES.meetingRoom.backgroundColor,
        borderColor: EVENT_STYLES.meetingRoom.borderColor,
        textColor: EVENT_STYLES.meetingRoom.textColor,
        classNames: 'im-meeting-room-busy',
      };
    });
  }, [data, roomNamesByEmail]);

  // Block the overlay only for the visible week's first fetch — once
  // we have data (even stale), the spinner drops and the calendar
  // reflects whatever is cached.
  const isBlockingBusyFetch =
    enabled && roomEmails.length > 0 && isFetching && !isSuccess;

  return { events, externalEvents, isBlockingBusyFetch, isLoading };
}
